import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import { CircularProgress, Tooltip } from "@mui/material";
import Button from "@components/Button/Button.tsx";
import ConfirmUnclaimDialog from "@components/ConfirmUnclaimDialog/ConfirmUnclaimDialog";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import Subheader from "@components/Subheader/Subheader.tsx";
import type {
  DateRangeValue,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { useNotification } from "@context/NotificationContext";
import { useFormattedReference } from "@hooks/useFormattedReference";
import PageLayout from "@layouts/PageLayout.tsx";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { GatePassListItem } from "@services/gatepass/types";
import { bulkUpdateGatepassTasks, getGatePasses } from "@services/gatepassDataApi";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { runTransaction } from "@telemetry/runTransaction";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSecurityClearanceValidation } from "../hooks/useSecurityClearanceValidation";
import BulkApproveGatepassDrawer from "./form/BulkApproveGatepassDrawer";
import BulkRejectGatepassDrawer from "./form/BulkRejectGatepassDrawer";
import { getGatepassCategoryTranslationKey, getGatePassesFilterFields } from "./utils";

const LOCAL_STORAGE_PAGE_KEY = "gatepass-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "gatepass-page-size";
const SEARCH_INQUIRY_ID = "gatepass.gatepasses.search";
const SEARCH_INQUIRY_CONTEXT = {
  module: "gatepass",
  screen: "Gatepasses",
  action: "search_gatepasses",
  entityType: "gatepass",
};
const DEFAULT_GATEPASS_CATEGORY_VALUES = ["NORMAL", "OFFSHORE", "TRUCK", "CARGO"];
const HAZARD_TYPES = ["Yes", "No"];
const EMPTY_ARRAY: unknown[] = [];

type SponsorCompany = {
  companyCode: string;
  companyNameEnglish: string;
};

const DATE_TIME_WITHOUT_TIMEZONE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/;

const toIsoDateOrNull = (
  value: string | DateRangeValue,
  options?: { treatDateTimeWithoutTimezoneAsUtc?: boolean },
) => {
  const raw = typeof value === "string" ? value : (value?.from ?? "");
  if (!raw) return null;
  const shouldTreatAsUtc =
    options?.treatDateTimeWithoutTimezoneAsUtc && DATE_TIME_WITHOUT_TIMEZONE_REGEX.test(raw);
  const d = new Date(shouldTreatAsUtc ? `${raw}Z` : raw);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};
const GatepassContainer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const { user, userActionPermissions, requestGatepassLabelKey } = useAuthStore();
  const {
    isValid: hasValidSecurityClearance,
    isLoading,
    validationMessage,
  } = useSecurityClearanceValidation();

  const currentUsername = user?.preferredUsername ?? "";
  const userRoles = user?.roles ?? [];
  const isFOTT = userRoles.includes("fott-user");
  const isSecurityDepartment = userRoles.some((role) =>
    ["security-operator", "security-supervisor"].includes(role),
  );
  const canSeeAddGatepassButton =
    !isFOTT || checkPermission(userActionPermissions, "canRequestGatepass");

  const { options: companies } = useFormattedReference<SponsorCompany>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isLicensedCompany=true`,
    "bff-gatepass",
  );

  const companyCodeToName = useMemo(
    () => new Map(companies.map((c) => [c.companyCode, c.companyNameEnglish])),
    [companies],
  );

  const availableFields = useMemo(() => {
    const fields = getGatePassesFilterFields(t);

    const gatepassCategoryOptions = Array.from(new Set(DEFAULT_GATEPASS_CATEGORY_VALUES)).map(
      (value) => ({
        value,
        label: t(getGatepassCategoryTranslationKey(value)),
      }),
    );

    const gatepassSponsorCompaniesOptions = companies.map((company) => ({
      value: company.companyCode,
      label: company.companyNameEnglish,
    }));

    const hazardOptions = HAZARD_TYPES.map((item) => ({
      label: t(`common.${item.toLowerCase()}`),
      value: item,
    }));

    fields.gatepassCategory = {
      label: t("gatepass.tableHeaders.gatepassCategory"),
      type: "select",
      options: gatepassCategoryOptions,
    };

    fields.sponsorCompanies = {
      label: t("gatepass.sponsor-company"),
      type: "autocomplete",
      options: gatepassSponsorCompaniesOptions,
    };

    fields.hazard = {
      label: t("gatepass.hazard"),
      type: "select",
      options: hazardOptions,
    };

    if (isSecurityDepartment) return fields;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { claimedBy: _omit, ...rest } = fields;

    return rest;
  }, [t, isSecurityDepartment, companies]);

  const headers: Header[] = useMemo(
    () => [
      { field: "gatepassId", columnName: t("gatepass.tableHeaders.gatepassId") },
      { field: "gatePassNo", columnName: t("gatepass.tableHeaders.gatePassNo") },
      { field: "groupNo", columnName: t("gatepass.tableHeaders.groupNo") },
      { field: "gatepassCategory", columnName: t("gatepass.tableHeaders.gatepassCategory") },
      { field: "sponsorCompany", columnName: t("gatepass.tableHeaders.sponsorCompany") },
      {
        field: "passportNumber",
        columnName: t("gatepass.passport-no-or-emirates-id"),
      },
      {
        field: "emiratesIdNumber",
        columnName: t("gatepass.emitates-id"),
      },
      { field: "companyName", columnName: t("gatepass.tableHeaders.companyName") },
      { field: "individualName", columnName: t("gatepass.tableHeaders.individualName") },
      {
        field: "submittedAt",
        columnName: t("gatepass.tableHeaders.submittedAt"),
        type: "date",
      },
      {
        field: "hazard",
        columnName: t("gatepass.tableHeaders.hazard"),
      },
      {
        field: "serviceNo",
        columnName: t("gatepass.tableHeaders.serviceNo"),
      },
      {
        field: "jobNo",
        columnName: t("gatepass.tableHeaders.jobNo"),
      },
      ...(isSecurityDepartment
        ? [
            {
              field: "claimedBy",
              columnName: t("gatepass.tableHeaders.claimedBy"),
            } as Header,
          ]
        : []),
      { field: "status", columnName: t("gatepass.gatepass-status") },
    ],
    [t, isSecurityDepartment],
  );

  const buildFilterBodyFromUI = (filters: FilterItem[]) => {
    const body: Record<string, any> = {};

    for (const f of filters) {
      if (!f.field || f.value === "" || f.value == null) continue;

      switch (f.field) {
        case "serialNumbers":
          body.serialNumbers = [...(body.serialNumbers ?? []), Number(f.value)];
          break;
        case "sequenceNumbers":
          body.sequenceNumbers = [...(body.sequenceNumbers ?? []), Number(f.value)];
          break;
        case "sequenceGroupNumbers":
          body.sequenceGroupNumbers = [...(body.sequenceGroupNumbers ?? []), Number(f.value)];
          break;
        case "gatepassCategory":
          body.gatepassCategories = [...(body.gatepassCategories ?? []), f.value];
          break;
        case "sponsorCompanies":
          body.sponsorCompanies = [...(body.sponsorCompanies ?? []), f.value];
          break;
        case "passportNumber":
          body.passportNumbers = [...(body.passportNumbers ?? []), f.value];
          break;
        case "fullNameEnglish":
          body.fullNamesEnglish = [...(body.fullNamesEnglish ?? []), f.value];
          break;
        case "gatepassStatus":
          body.gatepassStatuses = [...(body.gatepassStatuses ?? []), f.value];
          break;
        case "emiratesIdNumber":
          body.emiratesIdNumbers = [...(body.emiratesIdNumbers ?? []), f.value];
          break;
        case "companyName":
          body.companyNames = [...(body.companyNames ?? []), f.value];
          break;
        case "submittedAt": {
          const valueAsRange = f.value as { from?: string; to?: string };
          body[`${f.field}From`] = toIsoDateOrNull(valueAsRange.from ?? "");
          body[`${f.field}To`] = toIsoDateOrNull(valueAsRange.to ?? "", {
            treatDateTimeWithoutTimezoneAsUtc: true,
          });
          break;
        }
        case "hazard": {
          body.hazard = f.value === "Yes";
          break;
        }
        case "serviceNumbers":
          body.serviceNumbers = [...(body.serviceNumbers ?? []), f.value];
          break;
        case "jobNumbers":
          body.jobNumbers = [...(body.jobNumbers ?? []), f.value];
          break;

        case "claimedBy":
          body.claimedBy = [...(body.claimedBy ?? []), f.value];
          break;

        default:
          break;
      }
    }

    return body;
  };
  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? Number.parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? Number.parseInt(saved, 10) : 25;
  });

  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = localStorage.getItem("gatepasses-filters");
    return saved ? JSON.parse(saved) : [];
  });

  const onApplyFilters = (f: FilterItem[]) => {
    startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    setFilters(f);
    localStorage.setItem("gatepasses-filters", JSON.stringify(f));
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  const onClearFilters = () => {
    if (filters.length) {
      startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    }
    setFilters([]);
    localStorage.removeItem("gatepasses-filters");
  };

  const {
    data,
    isLoading: isGatepassesLoading,
    isFetching: isGatepassesFetching,
    isError: isGatepassesError,
    isSuccess: isGatepassesSuccess,
    dataUpdatedAt: gatepassesDataUpdatedAt,
    error: gatepassesError,
  } = useQuery({
    queryKey: ["gatepasses", page, pageSize, filters],
    queryFn: () => {
      const filterBody = buildFilterBodyFromUI(filters);
      return getGatePasses({
        service: "bff-gatepass",
        page: page - 1,
        size: pageSize,
        filters: filterBody,
      });
    },
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
  useTrackPendingInquiry(SEARCH_INQUIRY_ID, {
    isFetching: isGatepassesFetching,
    isLoading: isGatepassesLoading,
    isError: isGatepassesError,
    isSuccess: isGatepassesSuccess,
    error: gatepassesError,
    dataUpdatedAt: gatepassesDataUpdatedAt,
  });

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);

  useEffect(() => {
    if (isGatepassesLoading || gatepassesError || hasTrackedInitialLoad.current) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    completeScreenNavigation("Gatepass");
    trackScreenLoad("Gatepass", "Gatepass", startedAt.current);
  }, [gatepassesError, isGatepassesLoading]);

  const gatepasses = data?.gatepasses ?? (EMPTY_ARRAY as GatePassListItem[]);
  const totalCount = data?.totalRecordCount ?? 0;

  const formattedFormData = useMemo(
    () =>
      gatepasses.map((row) => {
        const statusCode = row.status ?? "";
        const isTruckCategory = String(row.gatepassCategory ?? "").toUpperCase() === "TRUCK";
        const resolvedGatepassCategory =
          isTruckCategory && row.truckType
            ? String(row.truckType)
            : String(row.gatepassCategory ?? "");

        return {
          id: row.id,
          gatePassNo: row.gatePassNo,
          gatepassId: row.gatepassId,
          groupNo: row.groupNo,
          gatepassCategory: resolvedGatepassCategory,
          sponsorCompany: companyCodeToName.get(row.sponsorCompany) ?? row.sponsorCompany,
          passportNumber: row.passportNumber,
          emiratesIdNumber: row.emiratesIdNumber,
          companyName: row.companyName,
          individualName: row.individualName,
          submittedAt: row.submittedAt,
          status: t(`gatepass.status-labels.${statusCode}`, {
            defaultValue: t("gatepass.status-labels.PROCESSING"),
          }),
          statusCode,
          claimedBy: row.claimedByPreferredUsername ?? row.task?.claimOwnerName ?? null,
          taskStatus: row.task?.status ?? null,
          taskId: row.task?.taskId ?? null,
          hazard: row.hazard,
          serviceNo: row.serviceNo,
          jobNo: row.jobNo,
        };
      }),
    [gatepasses, companyCodeToName, t],
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectedRows = useMemo(
    () => formattedFormData.filter((r) => selectedIds.includes(r.id)),
    [formattedFormData, selectedIds],
  );
  const [bulkApproveDrawerOpen, setBulkApproveDrawerOpen] = useState(false);
  const [bulkRejectDrawerOpen, setBulkRejectDrawerOpen] = useState(false);
  const [unclaimDialogOpen, setUnclaimDialogOpen] = useState(false);

  const isRowSelectable = useCallback(
    (row: (typeof formattedFormData)[number]) => {
      if (!checkPermission(userActionPermissions, "hasGatepassCheckbox") || !isSecurityDepartment) {
        return false;
      }

      const statusCode = row.statusCode as string | undefined;
      const taskStatus = row.taskStatus as string | undefined;
      const claimedBy = (row.claimedBy as string | null) ?? null;

      if (statusCode !== "AWAITING_SECURITY_DEPT_APPROVAL") return false;

      if (!claimedBy) {
        return taskStatus === "READY";
      }

      return claimedBy === currentUsername;
    },
    [userActionPermissions, isSecurityDepartment, currentUsername],
  );

  useEffect(() => {
    if (!selectedIds.length) return;

    const validIds = selectedIds.filter((id) => {
      const row = formattedFormData.find((r) => r.id === id);
      return row ? isRowSelectable(row) : false;
    });

    if (validIds.length !== selectedIds.length) {
      setSelectedIds(validIds);
    }
  }, [formattedFormData, selectedIds, isRowSelectable]);

  const selectedOnlyUnclaimed =
    selectedRows.length > 0 && selectedRows.every((row) => !row.claimedBy);
  const selectedOnlyClaimedByCurrentUser =
    selectedRows.length > 0 &&
    selectedRows.every((row) => row.claimedBy && row.claimedBy === currentUsername);

  const handleGatepassSelectionChange = useCallback((rows: Array<{ id: string }>) => {
    setSelectedIds(rows.map((r) => r.id));
  }, []);

  const handleGatepassPageChange = useCallback(
    (newPage: number, newSize: number) => {
      if (newPage !== page || newSize !== pageSize) {
        startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
      }
      setPage(newPage);
      setPageSize(newSize);
      localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
      localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
    },
    [page, pageSize],
  );

  const bulkClaimMutation = useMutation({
    mutationFn: (taskIds: string[]) =>
      runTransaction(
        {
          module: "gatepass",
          screen: "Gatepasses",
          action: "bulk_claim_gatepass",
          entityType: "gatepass",
        },
        (transactionId) =>
          bulkUpdateGatepassTasks({
            service: "bff-gatepass",
            taskIds,
            taskOperation: "CLAIM",
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["gatepasses"] });
      const successCount = data?.successfulCount ?? 0;
      const failCount = data?.failedCount ?? 0;
      if (failCount > 0) {
        notify({
          title: t("gatepass.bulk-claim-partial", {
            defaultValue: "Bulk claim completed with errors",
          }),
          description: t("gatepass.bulk-claim-partial-detail", {
            defaultValue: "{{success}} claimed, {{failed}} failed.",
            success: successCount,
            failed: failCount,
          }),
          type: "warning",
        });
      } else {
        notify({
          title: t("gatepass.claim-success", {
            defaultValue:
              "You’ve claimed this task. Your claim remains active for 30 minutes of task activity.",
          }),
          type: "success",
        });
      }
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const bulkUnclaimMutation = useMutation({
    mutationFn: (taskIds: string[]) =>
      runTransaction(
        {
          module: "gatepass",
          screen: "Gatepasses",
          action: "bulk_unclaim_gatepass",
          entityType: "gatepass",
        },
        (transactionId) =>
          bulkUpdateGatepassTasks({
            service: "bff-gatepass",
            taskIds,
            taskOperation: "UNCLAIM",
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["gatepasses"] });
      const successCount = data?.successfulCount ?? 0;
      const failCount = data?.failedCount ?? 0;
      if (failCount > 0) {
        notify({
          title: t("gatepass.bulk-unclaim-partial", {
            defaultValue: "Bulk unclaim completed with errors",
          }),
          description: t("gatepass.bulk-unclaim-partial-detail", {
            defaultValue: "{{success}} unclaimed, {{failed}} failed.",
            success: successCount,
            failed: failCount,
          }),
          type: "warning",
        });
      } else {
        notify({
          title: t("gatepass.unclaim-success", {
            defaultValue: "Task unclaimed. Other users can now claim it.",
          }),
          type: "success",
        });
      }
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
    onSettled: () => {
      setUnclaimDialogOpen(false);
    },
  });

  const isBulkActionInProgress = bulkClaimMutation.isPending || bulkUnclaimMutation.isPending;
  return (
    <PageLayout dir={dir}>
      <div className="relative">
        <LoadingOverlay
          loading={isGatepassesLoading || (isGatepassesFetching && !isBulkActionInProgress)}
        />
        <Subheader
          testId="gatepasses-subheader"
          subtitle={t("gatepass.gatepass-subtitle")}
          title={
            <div className="flex items-end justify-between w-full">
              <span>{t("gatepass.gatepass-title")}</span>
              {canSeeAddGatepassButton && (
                <Tooltip
                  title={
                    hasValidSecurityClearance ||
                    checkPermission(userActionPermissions, "canAlwaysRequestGatepass")
                      ? ""
                      : validationMessage
                  }
                  arrow
                  placement="top"
                >
                  <span>
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      testId="gastepass-create-button"
                      disabled={
                        isLoading ||
                        (!hasValidSecurityClearance &&
                          !checkPermission(userActionPermissions, "canAlwaysRequestGatepass"))
                      }
                      label={
                        <span className="text-sm font-medium flex items-center gap-2">
                          <AddCircleOutlineIcon fontSize="small" />
                          {isLoading ? t("common.loading") : t(requestGatepassLabelKey)}
                        </span>
                      }
                      sx={{
                        textTransform: "none",
                        height: "40px",
                        minHeight: "40px",
                      }}
                      onClick={() => {
                        const canRequest =
                          hasValidSecurityClearance ||
                          checkPermission(userActionPermissions, "canAlwaysRequestGatepass");
                        if (canRequest) {
                          navigate(`/${paths.gatepass.index}/${paths.gatepass.requestGatepass}`);
                        }
                      }}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          }
        />
        <div className="mt-8 px-24">
          <CustomDataTable
            hasCheckboxes={checkPermission(userActionPermissions, "hasGatepassCheckbox")}
            title={t("gatepass.gatepasses-title")}
            actionName={t(requestGatepassLabelKey)}
            testId="gatepasses-table"
            headers={headers}
            rows={formattedFormData}
            isLoading={isGatepassesLoading}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            canPreview={
              !isBulkActionInProgress &&
              checkPermission(userActionPermissions, "canPreviewGatepassRequest")
            }
            onPageChange={handleGatepassPageChange}
            navigationLinkBase={`/${paths.gatepass.index}/${paths.gatepass.gatepassRequestPreview}`}
            filters={filters}
            onApplyFilters={onApplyFilters}
            onClearFilters={onClearFilters}
            availableFields={availableFields}
            isRowSelectable={isRowSelectable}
            onSelectionChange={handleGatepassSelectionChange}
            headerContent={
              selectedRows.length > 0 &&
              checkPermission(userActionPermissions, "hasGatepassCheckbox") &&
              isSecurityDepartment ? (
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">
                    {t("gatepass.actions-label", "Actions")}:
                  </span>
                  {selectedOnlyUnclaimed && (
                    <Button
                      type="button"
                      variant="contained"
                      startIcon={<ContentPasteGoIcon />}
                      color="primary"
                      label={
                        <span className="text-sm font-medium normal-case">
                          {t("gatepass.bulk-claim", { defaultValue: "Claim" })}
                        </span>
                      }
                      onClick={() => {
                        const taskIds = selectedRows
                          .map((r) => r.taskId as string | null)
                          .filter((id): id is string => Boolean(id));
                        if (taskIds.length) {
                          bulkClaimMutation.mutate(taskIds);
                        }
                      }}
                      disabled={isBulkActionInProgress}
                    />
                  )}
                  {selectedOnlyClaimedByCurrentUser && (
                    <>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        label={
                          <span className="text-sm font-medium normal-case">
                            {t("gatepass.bulk-unclaim", { defaultValue: "Unclaim" })}
                          </span>
                        }
                        onClick={() => setUnclaimDialogOpen(true)}
                        disabled={isBulkActionInProgress}
                      />
                      <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        startIcon={<ContentPasteGoIcon />}
                        label={
                          <span className="text-sm font-medium normal-case">
                            {t("gatepass.approve", { defaultValue: "Approve" })}
                          </span>
                        }
                        onClick={() => setBulkApproveDrawerOpen(true)}
                      />
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        startIcon={<CancelOutlinedIcon />}
                        label={
                          <span className="text-sm font-medium normal-case">
                            {t("gatepass.reject", { defaultValue: "Reject" })}
                          </span>
                        }
                        onClick={() => setBulkRejectDrawerOpen(true)}
                      />
                    </>
                  )}
                </div>
              ) : null
            }
          />
        </div>
      </div>
      {isBulkActionInProgress && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-white/40">
          <CircularProgress />
        </div>
      )}
      <ConfirmUnclaimDialog
        isOpen={unclaimDialogOpen}
        setIsOpen={setUnclaimDialogOpen}
        confirmAction={() => {
          const taskIds = selectedRows
            .map((r) => r.taskId as string | null)
            .filter((id): id is string => Boolean(id));
          if (taskIds.length) {
            bulkUnclaimMutation.mutate(taskIds);
          }
        }}
        count={selectedRows.length}
        languageBase="gatepass"
        testId="gatepass-bulk-unclaim-confirm-dialog"
        isBulk
      />
      <BulkApproveGatepassDrawer
        isOpen={bulkApproveDrawerOpen}
        onClose={() => setBulkApproveDrawerOpen(false)}
        selectedRows={selectedRows.map((r) => ({
          id: r.id,
          gatepassId: r.gatepassId,
          taskId: (r.taskId as string | null) ?? null,
        }))}
        onSuccess={async () => {
          await queryClient.invalidateQueries({ queryKey: ["gatepasses"] });
        }}
      />
      <BulkRejectGatepassDrawer
        isOpen={bulkRejectDrawerOpen}
        onClose={() => setBulkRejectDrawerOpen(false)}
        selectedRows={selectedRows.map((r) => ({
          id: r.id,
          gatepassId: r.gatepassId,
          taskId: (r.taskId as string | null) ?? null,
        }))}
        onSuccess={async () => {
          await queryClient.invalidateQueries({ queryKey: ["gatepasses"] });
        }}
      />
    </PageLayout>
  );
};

export default GatepassContainer;
